import React, { useState } from 'react';
import { useDispatch } from "react-redux";
import { authentication } from "@microsoft/teams-js";
import { TrackJS } from "trackjs";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { microsoftRequest } from '@shared/services/EnvironmentVariables';
import { failureNotification, successNotification } from "@shared/store/notification.duck";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

interface Props {
  onConsentGranted?: () => void;
}

export function RequestAdminConsentButton({ onConsentGranted }: Props) {
  const dispatch = useDispatch();
  const [pending, setPending] = useState(false);

  const onClick = async () => {
    if (pending) return;
    setPending(true);
    const scopes = microsoftRequest.login.scopes.join(' ');
    try {
      const result = await authentication.authenticate({
        url: `${window.location.origin}/login/admin-consent?scopes=${encodeURIComponent(scopes)}`,
        width: 600,
        height: 600,
        isExternal: true,
      });
      const parsed = JSON.parse(result || '{}') as { adminConsent?: boolean };
      if (parsed.adminConsent) {
        dispatch(successNotification('Admin approval granted. Please sign in again.'));
        onConsentGranted?.();
      } else {
        dispatch(failureNotification('Admin approval did not complete. Please try again.'));
      }
    } catch (e: any) {
      const msg = (e?.message ?? e?.toString?.() ?? '').toString();
      if (msg.includes('CancelledByUser')) {
        return;
      }
      TrackJS?.track(`[RequestAdminConsentButton] failed: ${msg.slice(0, 200)}`);
      dispatch(failureNotification('Failed to start admin approval. Please try again or contact support.'));
    } finally {
      setPending(false);
    }
  };

  return (
    <OutlineButton
      dataTest="requestAdminConsent"
      className="login__button"
      text="login.request-admin-approval"
      click={onClick}
      fullWidth={true}
      size={ButtonSize.Large}
    />
  );
}
